import { useState } from "react";
import { FileDropZone } from "@/components/documents/FileDropZone";
import { DocumentList } from "@/components/documents/DocumentList";
import { DocumentCategory, useDocuments } from "@/contexts/DocumentContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FileText, Image, File, FolderOpen } from "lucide-react";

const CATEGORIES: DocumentCategory[] = ["Invoice", "Purchase Bill", "Expense", "Payment", "GST Filing", "Other"];

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Invoice: FileText,
  "Purchase Bill": File,
  Expense: FileText,
  Payment: FileText,
  "GST Filing": FileText,
  Other: FolderOpen,
};

const DocumentsPage = () => {
  const { documents } = useDocuments();
  const [activeCategory, setActiveCategory] = useState<DocumentCategory>("Invoice");

  const stats = {
    total: documents.length,
    images: documents.filter((d) => d.type.startsWith("image/")).length,
    pdfs: documents.filter((d) => d.type === "application/pdf").length,
    totalSize: documents.reduce((s, d) => s + d.size, 0),
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Documents</h1>
        <p className="text-sm text-muted-foreground">Upload and manage documents attached to financial records</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Files", value: stats.total, icon: FolderOpen },
          { label: "PDFs", value: stats.pdfs, icon: FileText },
          { label: "Images", value: stats.images, icon: Image },
          { label: "Storage Used", value: stats.totalSize > 0 ? `${(stats.totalSize / (1024 * 1024)).toFixed(1)} MB` : "0 MB", icon: File },
        ].map((stat) => (
          <Card key={stat.label} className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <stat.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{stat.value}</p>
                <p className="text-[11px] text-muted-foreground">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upload Panel */}
        <Card className="lg:col-span-1 border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Upload Documents</CardTitle>
            <CardDescription className="text-xs">Select a category and upload files</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">Category</label>
              <div className="grid grid-cols-2 gap-1.5">
                {CATEGORIES.map((cat) => {
                  const Icon = CATEGORY_ICONS[cat] || File;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all border ${
                        activeCategory === cat
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border text-muted-foreground hover:border-primary/30 hover:bg-muted/30"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
            <FileDropZone category={activeCategory} />
          </CardContent>
        </Card>

        {/* Documents List */}
        <Card className="lg:col-span-2 border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">All Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList className="mb-4">
                <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                {CATEGORIES.map((cat) => (
                  <TabsTrigger key={cat} value={cat} className="text-xs">{cat}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="all">
                <DocumentList showCategory />
              </TabsContent>
              {CATEGORIES.map((cat) => (
                <TabsContent key={cat} value={cat}>
                  <DocumentList documents={documents.filter((d) => d.category === cat)} showCategory={false} />
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DocumentsPage;
